import { ChangeEvent, useEffect, useState } from "react";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

import { DEFAULT_COLOR } from "../global";
import useData from "../app/useData";
import Box from "../library/box";
import Desktop from "../library/desktop";
import Table from "../library/table";
import Reel from "../library/reel";

import ButtonIcon from "./ButtonIcon";
import Form from "./Form";
import ElementListAddBox from "./ElementListAddBox";
import ElementListAddTable from "./ElementListAddTable";
import ElementListAddReel from "./ElementListAddReel";
import ElementListAddColor from "./ElementListAddColor";

import styles from "./ElementListAdd.module.css";
import elementFromJson from "../library/deserialize";

export type UpdateFuncType = (
	event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
	{ name, value }: { name: string; value: string | number }
) => void;

const ElementListAdd: React.FC<{}> = () => {
	const { elementEditing, unselectElement, newElement } = useData();

	const [editing, setEditing] = useState<ElementDefinitionType>();

	const elementId = elementEditing?.elementId;
	useEffect(() => {
		if (elementId) {
			setEditing(undefined);
		}
	}, [elementId]);
	// TODO should call a validator first to be sure it has been added,
	// otherwise we clean the form but the element was not added because invalid (wrong prop, name already exists...)
	const validateElement = () => {
		if (editing) {
			newElement(editing);
			setEditing(undefined);
		}
	};

	const updateEditing: UpdateFuncType = (_, { name, value }) =>
		setEditing(editing => editing && editing.cloneUpdate(name, value));
	return (
		<div className={styles.container}>
			<div
				className={styles.header}
				onClick={
					editing
						? undefined
						: () => {
								unselectElement();
								setEditing(
									new Box({
										id: "",
										color: DEFAULT_COLOR,
										width: 0,
										height: 0,
										depth: 0
									})
								);
						  }
				}
			>
				<span>Add...</span>
				{editing && (
					<>
						<ButtonIcon icon={faCheck} onClick={validateElement} />
						<ButtonIcon icon={faTimes} onClick={() => setEditing(undefined)} />
					</>
				)}
			</div>
			{editing && (
				<Form>
					<Form.Labels>
						<Form.Field>Id</Form.Field>
						<Form.Field>Type</Form.Field>
						<Form.Field>Color</Form.Field>
					</Form.Labels>
					<Form.Values>
						<Form.Input
							name="id"
							type="text"
							value={editing.id}
							onChange={updateEditing}
						/>
						<Form.Select
							name="type"
							value={editing.type}
							options={[
								{ text: "Box", value: "box" },
								{ text: "Table", value: "table" },
								{ text: "Desktop", value: "desktop" },
								{ text: "Reel", value: "reel" }
							]}
							onChange={(_, { value }) =>
								setEditing(
									editing =>
										editing &&
										elementFromJson({ ...editing.toJson(), type: value })
								)
							}
						/>
						<ElementListAddColor
							name="color"
							value={editing.color}
							onChange={updateEditing}
						/>
					</Form.Values>
				</Form>
			)}
			<ElementListAddDetails element={editing} onUpdate={updateEditing} />
		</div>
	);
};
const ElementListAddDetails: React.FC<{
	element?: ElementDefinitionType;
	onUpdate: UpdateFuncType;
}> = ({ element, onUpdate }) => {
	if (!element) {
		return null;
	}
	if (element instanceof Box) {
		return <ElementListAddBox element={element} onUpdate={onUpdate} />;
	} else if (element instanceof Table || element instanceof Desktop) {
		return <ElementListAddTable element={element} onUpdate={onUpdate} />;
	} else if (element instanceof Reel) {
		return <ElementListAddReel element={element} onUpdate={onUpdate} />;
	}
	return null;
};

export default ElementListAdd;
